import { SystemProp, Theme, ThemeSize, x } from '@xstyled/styled-components';
import * as CSS from 'csstype';
import React, { useEffect, useState } from 'react';
import { Breakpoints } from '~/lib/useResponsive';
import { Item, ItemDefinition, registry, RenderProps } from '../Item';
import { CARD } from '../itemIds';
import { useEditorStore } from '~/shell/Store';
import TrashIcon from '~/assets/icons/trashIcon.svg';
import { t } from 'ttag';

const shadow =
  '0px -2px 4px rgba(0, 0, 0, 0.05), 0px 30px 30px rgba(0, 0, 0, 0.03), 0px 15px 20px rgba(0, 0, 0, 0.05)';

interface Props {
  width?: SystemProp<ThemeSize<Theme> | CSS.Property.Width, Theme>;
  height?: SystemProp<ThemeSize<Theme> | CSS.Property.Height, Theme>;
  padding?: SystemProp<ThemeSize<Theme> | CSS.Property.Padding, Theme>;
  clearStyles?: Breakpoints;
  borderRadius?: SystemProp<
    ThemeSize<Theme> | CSS.Property.BorderRadius,
    Theme
  >;
  className?: string;
  type?: string;
  minHeight?: string;
  maxHeight?: string;
  canWeDeleteCard?: boolean;
}

export type CardDefinition = ItemDefinition<typeof CARD, Props>;

export class Card extends Item<typeof CARD, Props> {
  protected render({
    width = '360px',
    height = '514px',
    padding = '30px',
    borderRadius = 'xl',
    clearStyles,
    children,
    className,
    type = 'div',
    minHeight,
    maxHeight,
    canWeDeleteCard,
    ...props
  }: RenderProps<Props>) {
    const { removeSubItem, sequence } = useEditorStore();
    const [showItem, setShowItem] = useState(!props.self.start);
    const [canWeShowDeleteButton, setCanWeShowDeleteButton] =
      useState(canWeDeleteCard);

    useEffect(() => {
      if (sequence && canWeDeleteCard) {
        const cardItems = sequence.items.filter(item => item.type == 'CARD');

        if (cardItems.length < 2) {
          setCanWeShowDeleteButton(false);
        }
      }
    }, [sequence, canWeDeleteCard]);

    useEffect(() => {
      if (!props.self.start) return;
      const timeout = setTimeout(
        () => setShowItem(true),
        props.self.start * 1000
      );
      return () => clearTimeout(timeout);
    }, [props.self.start]);

    return (
      <x.div
        as={type as never}
        w={width}
        h={height}
        minH={minHeight || 'unset'}
        maxH={maxHeight || 'unset'}
        p={padding}
        display={showItem ? 'flex' : 'none'}
        bg={
          clearStyles ? { _: 'white', [clearStyles]: 'transparent' } : 'white'
        }
        borderRadius={borderRadius}
        boxShadow={clearStyles ? { _: shadow, [clearStyles]: 'none' } : shadow}
        overflow="hidden"
        maxW={{ _: '330px', md: 'unset' }}
        className={className}
        aria-hidden={!showItem}
        position="relative"
      >
        {canWeShowDeleteButton && (
          <x.button
            type="button"
            aria-label={t`Delete card`}
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              display: 'flex',
              position: 'absolute',
              top: '15px',
              left: '15px',
              zIndex: 10
            }}
            onClick={() => removeSubItem(props.id)}
          >
            <TrashIcon width={16} height={16} />
          </x.button>
        )}
        {children}
      </x.div>
    );
  }
}

registry[CARD] = Card;
